import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  AidTypeInfo,
  BeneficiarySummary,
  HelpRequestSummary,
  Paginated,
} from '@foundation/shared';
import { HELP_REQUEST_STATUSES, HELP_REQUEST_STATUS_LABEL_TH } from '@foundation/shared';
import { api, buildQuery } from '../lib/api';
import { formatDateTime } from '../lib/format';
import { Empty, ErrorAlert, Loading, Modal } from '../components/ui';
import { useAuth } from '../auth';

const STATUS_TONE: Record<string, string> = {
  new: 'amber',
  screening: 'blue',
  accepted: 'green',
  rejected: '',
  duplicate: '',
};

export default function HelpRequestList() {
  const { allowed } = useAuth();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState<HelpRequestSummary[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [accepting, setAccepting] = useState<HelpRequestSummary | null>(null);

  const status = params.get('status') ?? 'new';

  const load = useCallback(
    async (append = false, from?: string) => {
      setError(null);
      try {
        const res = await api<Paginated<HelpRequestSummary>>(
          `/help-requests${buildQuery({ status, limit: 50, cursor: from })}`,
        );
        setRows((prev) => (append && prev ? [...prev, ...res.data] : res.data));
        setCursor(res.nextCursor);
      } catch (e) {
        setError(e);
        if (!append) setRows([]);
      }
    },
    [status],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function screen(id: string, next: 'screening' | 'rejected' | 'duplicate') {
    const reason =
      next === 'screening' ? undefined : window.prompt('เหตุผล (บันทึกไว้ถาวร)') ?? undefined;
    if (next !== 'screening' && !reason) return;
    try {
      await api(`/help-requests/${id}/screen`, {
        method: 'POST',
        body: { status: next, reason },
      });
      await load();
    } catch (e) {
      setError(e);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>คำขอความช่วยเหลือ</h1>
          <p>เรื่องที่ประชาชนส่งเข้ามาทาง LINE — คัดกรองแล้วรับเป็นเคส</p>
        </div>
      </div>

      <ErrorAlert error={error} />

      <div className="card">
        <div className="toolbar">
          <div className="field">
            <label htmlFor="hstatus">สถานะ</label>
            <select
              id="hstatus"
              value={status}
              onChange={(e) => {
                const next = new URLSearchParams(params);
                next.set('status', e.target.value);
                setParams(next, { replace: true });
              }}
            >
              <option value="">ทั้งหมด</option>
              {HELP_REQUEST_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {HELP_REQUEST_STATUS_LABEL_TH[s]}
                </option>
              ))}
            </select>
          </div>
        </div>

        {rows === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>ไม่มีคำขอในสถานะนี้</Empty>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>เข้ามาเมื่อ</th>
                    <th className="wrap">ผู้ขอ</th>
                    <th className="wrap">รายละเอียด</th>
                    <th>พื้นที่</th>
                    <th>สถานะ</th>
                    <th>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((h) => (
                    <tr key={h.id}>
                      <td className="small muted">{formatDateTime(h.createdAt)}</td>
                      <td className="wrap">
                        {h.contactName}
                        <div className="muted small">
                          {h.contactPhone ?? 'ไม่ระบุเบอร์'}
                          {h.lineLinked && ' · ผูก LINE แล้ว'}
                        </div>
                      </td>
                      <td className="wrap small">{h.detail}</td>
                      <td className="small">{h.areaName ?? h.addressText ?? '—'}</td>
                      <td>
                        <span className={`badge ${STATUS_TONE[h.status]}`}>
                          {HELP_REQUEST_STATUS_LABEL_TH[h.status]}
                        </span>
                        {h.rejectReason && (
                          <div className="muted small wrap">{h.rejectReason}</div>
                        )}
                      </td>
                      <td>
                        {allowed('beneficiary:write') && h.status !== 'accepted' && (
                          <div className="btn-row">
                            {h.status === 'new' && (
                              <button
                                className="btn ghost"
                                onClick={() => void screen(h.id, 'screening')}
                              >
                                รับไว้พิจารณา
                              </button>
                            )}
                            <button className="btn" onClick={() => setAccepting(h)}>
                              รับเป็นเคส
                            </button>
                            <button
                              className="btn ghost"
                              onClick={() => void screen(h.id, 'rejected')}
                            >
                              ไม่รับเรื่อง
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {cursor && (
              <div className="btn-row">
                <button className="btn ghost" onClick={() => void load(true, cursor)}>
                  โหลดเพิ่ม
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {accepting && (
        <AcceptModal
          request={accepting}
          onClose={() => setAccepting(null)}
          onDone={() => {
            setAccepting(null);
            void load();
          }}
        />
      )}
    </>
  );
}

function AcceptModal({
  request,
  onClose,
  onDone,
}: {
  request: HelpRequestSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [firstName, setFirstName] = useState(request.contactName.split(' ')[0] ?? '');
  const [lastName, setLastName] = useState(request.contactName.split(' ').slice(1).join(' '));
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<BeneficiarySummary[]>([]);
  const [picked, setPicked] = useState<BeneficiarySummary | null>(null);

  const [openCase, setOpenCase] = useState(true);
  const [aidTypes, setAidTypes] = useState<AidTypeInfo[]>([]);
  const [aidTypeId, setAidTypeId] = useState('');
  const [title, setTitle] = useState('');

  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<AidTypeInfo[]>('/meta/aid-types')
      .then(setAidTypes)
      .catch(() => setAidTypes([]));
  }, []);

  useEffect(() => {
    if (mode !== 'existing' || !search.trim()) return;
    const t = setTimeout(() => {
      api<Paginated<BeneficiarySummary>>(`/beneficiaries${buildQuery({ q: search, limit: 10 })}`)
        .then((r) => setResults(r.data))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search, mode]);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ caseId: string | null }>(`/help-requests/${request.id}/accept`, {
        method: 'POST',
        body: {
          ...(mode === 'existing'
            ? { beneficiaryId: picked?.id }
            : {
                createBeneficiary: {
                  firstName: firstName.trim(),
                  lastName: lastName.trim() || '-',
                  phone: request.contactPhone ?? undefined,
                  areaId: request.areaId ?? undefined,
                  addressLine: request.addressText ?? undefined,
                },
              }),
          ...(openCase && aidTypeId && title.trim()
            ? { openCase: { aidTypeId, title: title.trim() } }
            : {}),
        },
      });
      if (res.caseId) navigate(`/cases/${res.caseId}`);
      else onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const ready =
    (mode === 'existing' ? Boolean(picked) : firstName.trim().length > 0) &&
    (!openCase || (Boolean(aidTypeId) && title.trim().length > 0));

  return (
    <Modal title="รับคำขอเป็นเคส" onClose={onClose}>
      <ErrorAlert error={error} />

      <p className="hint wrap">{request.detail}</p>

      <div className="btn-row">
        <label className="checkbox">
          <input
            type="radio"
            checked={mode === 'new'}
            onChange={() => setMode('new')}
          />
          สร้างผู้รับความช่วยเหลือรายใหม่
        </label>
        <label className="checkbox">
          <input
            type="radio"
            checked={mode === 'existing'}
            onChange={() => setMode('existing')}
          />
          ผูกกับรายเดิมที่มีอยู่
        </label>
      </div>

      {mode === 'new' ? (
        <div className="form-row">
          <div className="field">
            <label htmlFor="hfn">ชื่อ</label>
            <input id="hfn" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="hln">นามสกุล</label>
            <input id="hln" value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </div>
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="hsearch">ค้นหารายเดิม</label>
            <input
              id="hsearch"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="ชื่อ รหัส หรือเบอร์โทร"
            />
          </div>
          {picked ? (
            <p>
              เลือกแล้ว: <strong>{picked.firstName} {picked.lastName}</strong> ({picked.benCode}){' '}
              <button className="link" onClick={() => setPicked(null)}>
                เปลี่ยน
              </button>
            </p>
          ) : (
            results.map((b) => (
              <div key={b.id} className="btn-row">
                <span>
                  {b.firstName} {b.lastName} · {b.benCode}
                </span>
                <button className="btn ghost" onClick={() => setPicked(b)}>
                  เลือก
                </button>
              </div>
            ))
          )}
        </>
      )}

      <label className="checkbox">
        <input type="checkbox" checked={openCase} onChange={(e) => setOpenCase(e.target.checked)} />
        เปิดเคสความช่วยเหลือต่อทันที
      </label>

      {openCase && (
        <div className="form-row">
          <div className="field">
            <label htmlFor="hat">ประเภทความช่วยเหลือ</label>
            <select id="hat" value={aidTypeId} onChange={(e) => setAidTypeId(e.target.value)}>
              <option value="">เลือก…</option>
              {aidTypes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field grow">
            <label htmlFor="hct">เรื่อง</label>
            <input id="hct" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
        </div>
      )}

      <p className="hint">
        เคสจะถูกมอบให้เจ้าหน้าที่ที่ดูแลผู้รับความช่วยเหลือรายนี้ ไม่ใช่ผู้ที่กดรับเรื่อง
        เพื่อให้ผู้อนุมัติกับผู้ยื่นเป็นคนละคนตามหลักแบ่งแยกหน้าที่
      </p>

      <div className="btn-row">
        <button className="btn" disabled={busy || !ready} onClick={() => void submit()}>
          รับเป็นเคส
        </button>
        <button className="btn ghost" onClick={onClose}>
          ยกเลิก
        </button>
      </div>
    </Modal>
  );
}
